import { Injectable } from '@angular/core';
import { AlertType } from '@gains-com/ui-angular';
import { ApiService } from './api.service';
import {
  Currency,
  CurrencyId,
  PaymentMethod,
  PaymentMethodEntityTypeId,
  PaymentProviderAccount,
  PaymentProviderId,
  User,
  UserBlockchainAddress,
} from '@gains-com/shared';
import {
  CoinflowCardMetadata,
  CoinflowDebitCardForm,
  CoinflowRedemptionPaymentMethod,
} from '../components/payments/common.js';

export interface PaymentMethodAndUserBlockchainAddress<METADATA = unknown>
  extends PaymentMethod<METADATA>,
    Pick<User, 'user_id'>,
    Pick<Currency, 'blockchain_id'>,
    Pick<
      PaymentProviderAccount,
      | 'payment_provider_account_entity_type_id'
      | 'payment_provider_account_entity_id'
      | 'approved_by_payment_provider'
    >,
    Partial<
      Pick<
        UserBlockchainAddress,
        'user_blockchain_address_id' | 'user_blockchain_address_format_id'
      >
    > {}

@Injectable({
  providedIn: 'root',
})
export class PaymentMethodsService extends ApiService {
  ErrorMap = {
    INTERNAL_SERVER_ERROR: {
      swallowError: false,
      title: 'Unknown Error',
      message:
        'An unknown problem occurred. Please contact customer support if the problem persists.',
      type: AlertType.Danger,
    },
    INVALID_PAYMENT_METHOD: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message:
        'Redemption payment method is invalid. Try using a different payment method.',
    },
    BLOCKCHAIN_ADDRESS_IN_USE: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'BlockchainId Error',
      message:
        'BlockchainId address is in use by another account. Try another blockchain address.',
    },
    KYC_FAILED: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'KYC Verification Failed',
      message: 'KYC verification failed. Please contact support.',
    },
    KYC_NOT_APPROVED: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'KYC Verification Not Approved',
      message: 'KYC verification not started. Please contact support.',
    },
    KYC_NOT_STARTED: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'KYC Verification Not Approved',
      message: 'KYC verification not started. Please contact support.',
    },
    ADDRESS_ALREADY_PURCHASE_ADDRESS: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Invalid Address',
      message:
        'This address is already registered as a purchase address. Please use a different address.',
    },
    KYC_NOT_VERIFIED: {
      swallowError: false,
    },
    PURCHASE_LIMIT_REACHED: {
      swallowError: false,
    },
    MODE_RESTRICTED: {
      swallowError: false,
    },
    ...Object.fromEntries(
      ['COULD_NOT_RECONCILE_PAYMENT_USER'].map((error) => [
        error,
        {
          type: AlertType.Danger,
          swallowError: false,
          title: 'Redemption Failed',
          message: 'An unexpected error occurred. Please try again.',
        },
      ])
    ),
  };

  async getPaymentMethods(query?: {
    entityTypes?: PaymentMethodEntityTypeId[];
  }) {
    const q = new URLSearchParams();

    if (query?.entityTypes) {
      query.entityTypes.forEach((entityType) =>
        q.append('entityTypes', entityType)
      );
    }

    return await this.get<PaymentMethodAndUserBlockchainAddress[]>(
      `/payment-methods/?${q.toString()}`
    );
  }

  async createFireblocksPurchaseAddresses(currencyId: CurrencyId) {
    return await this.post<PaymentMethodAndUserBlockchainAddress[]>(
      '/payment-methods/fireblocks-purchase-addresses',
      {
        currencyId,
      }
    );
  }

  async createFireblocksRedemptionAddress(
    currencyId: CurrencyId,
    address: string
  ) {
    return await this.post<{
      payment_method: PaymentMethodAndUserBlockchainAddress | null;
      address: string;
      currency_id: CurrencyId;
    }>(
      '/payment-methods/fireblocks-redemption-addresses',
      {
        currencyId,
        address,
      }
    );
  }

  async syncFireblocksRedemptionPaymentMethods() {
    return await this.post<void>(
      '/payment-methods/fireblocks-redemption-payment-methods',
      {}
    );
  }

  async syncCoinflowRedemptionPaymentMethods() {
    return await this.post<{
      payment_methods: CoinflowRedemptionPaymentMethod[];
      stale: boolean;
    }>(
      '/payment-methods/coinflow-redemption-payment-methods',
      {}
    );
  }

  async syncCoinflowPurchasePaymentMethods() {
    return await this.post<{
      payment_methods: PaymentMethod<CoinflowCardMetadata>[];
      stale: boolean;
    }>(
      '/payment-methods/coinflow-purchase-payment-methods',
      {}
    );
  }

  async linkVenmoAccount(phoneNumber: string) {
    return await this.post<void>(
      '/payment-methods/coinflow-venmo-account',
      { phoneNumber }
    );
  }

  async deletePaymentMethod(
    opts: {
      paymentMethodEntityId: string,
      paymentMethodEntityTypeId?: PaymentMethodEntityTypeId[],
      paymentProviderId?: PaymentProviderId[]
    }
  ) {
    return this.delete('/payment-methods', {
      body: {
        paymentMethodEntityId: [ opts.paymentMethodEntityId ],
        paymentMethodEntityTypeId: opts.paymentMethodEntityTypeId,
        paymentProviderId: opts.paymentProviderId
      },
      observe: 'events'
    })
  }

  async createCoinflowRedemptionDebitCard(form: CoinflowDebitCardForm) {
    return await this.post<PaymentMethodAndUserBlockchainAddress[]>(
      '/payment-methods/coinflow-redemption-debit-card',
      form
    );
  }

  async getPaymentMethodById(paymentMethodId: string) {
    return await this.get<PaymentMethodAndUserBlockchainAddress>(
      `/payment-methods/${paymentMethodId}`
    );
  }
}
